import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  TextField,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useTeacher } from '../context/TeacherContext';
import apiAxiosInstance from '../utils/axiosInstance';
import { Teacher } from '../Types/Teacher';
import { Standard } from '../Types/Standard';
import { SUBJECTS, DEFAULT_PRIMARY_SUBJECT } from '../config/subjects';

const CompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const { teacher, setTeacher } = useTeacher();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gradeLevel: '',
    teachingStandards: '',
    primarySubject: DEFAULT_PRIMARY_SUBJECT,
    teachingSubjects: [] as string[],
  });

  // Initialize form data from teacher context
  useEffect(() => {
    if (teacher) {
      console.log('Initializing form with teacher:', teacher);
      const standardId = teacher.teachingStandards?.[0]?._id ?? '';

      setFormData({
        firstName: teacher.firstName ?? '',
        lastName: teacher.lastName ?? '',
        gradeLevel: teacher.gradeLevel ?? '',
        teachingStandards: standardId,
        primarySubject: teacher.primarySubject ?? DEFAULT_PRIMARY_SUBJECT,
        teachingSubjects: teacher.teachingSubjects?.map(ts => ts.subject).filter(Boolean) ?? [],
      });
    }
  }, [teacher]);

  // Fetch standards separately
  useEffect(() => {
    const fetchStandards = async () => {
      try {
        const response = await apiAxiosInstance.get('/api/standards');
        setStandards(response.data);
        
        if (teacher?.teachingStandards?.[0] && response.data.length > 0) {
          // Type assertion here as well
          const standard = teacher.teachingStandards[0] as Standard;
          const standardId = standard._id ?? '';
          setFormData(prev => ({
            ...prev,
            teachingStandards: standardId
          }));
        }
      } catch (err) {
        console.error('Error fetching standards:', err);
      }
    };

    fetchStandards();
  }, [teacher]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultiSelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);

    try {
      // Debug log the current token
      const currentToken = localStorage.getItem('token');
      console.log('Current token:', currentToken);
      
      if (!teacher?._id) {
        throw new Error('No teacher ID available');
      }

      // Get the ID from the current teacher context
      const teacherId = teacher._id;
      console.log('Using teacher ID:', teacherId);

              const response = await apiAxiosInstance.patch(`/auth/complete-profile/${teacherId}`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        gradeLevel: formData.gradeLevel,
        teachingStandards: formData.teachingStandards,
        primarySubject: formData.primarySubject,
        teachingSubjects: formData.teachingSubjects,
      });

      if (response.data) {
        setTeacher(response.data);
        navigate('/edu-chat');
      }
    } catch (err: any) {
      console.error('Profile completion error:', err);
      // More detailed error message
      const errorMsg = err.response?.data?.message || 
                      `Error updating profile: ${err.message}`;
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '500px',
          borderRadius: 2,
          backgroundColor: '#fff',
        }}
      >
        <Typography 
          variant="h5" 
          component="h1" 
          gutterBottom 
          textAlign="center"
          color="primary"
          fontWeight="bold"
          mb={2}
        >
          Complete Your Profile
        </Typography>

        <Typography 
          variant="body1"
          textAlign="center"
          color="text.secondary"
          mb={4}
        >
          Let's complete your profile to better assist you
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleTextChange}
            margin="normal"
            required
            autoFocus
          />

          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleTextChange}
            margin="normal"
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Grade Level</InputLabel>
            <Select
              value={formData.gradeLevel}
              label="Grade Level"
              name="gradeLevel"
              onChange={handleSelectChange}
              required
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((grade) => (
                <MenuItem key={grade} value={grade.toString()}>
                  Grade {grade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Primary Subject</InputLabel>
            <Select
              value={formData.primarySubject}
              label="Primary Subject"
              name="primarySubject"
              onChange={handleSelectChange}
              required
            >
              {SUBJECTS.map((subject) => (
                <MenuItem key={subject.value} value={subject.value}>
                  {subject.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
              Your main teaching subject (used as default in chat)
            </Typography>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Other Subjects (Optional)</InputLabel>
            <Select
              multiple
              value={formData.teachingSubjects}
              label="Other Subjects (Optional)"
              name="teachingSubjects"
              onChange={handleMultiSelectChange}
              input={<OutlinedInput label="Other Subjects (Optional)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const subject = SUBJECTS.find(s => s.value === value);
                    return <Chip key={value} label={subject?.label || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {SUBJECTS.filter(s => s.value !== formData.primarySubject).map((subject) => (
                <MenuItem key={subject.value} value={subject.value}>
                  <Checkbox checked={formData.teachingSubjects.indexOf(subject.value) > -1} />
                  <ListItemText primary={subject.label} secondary={subject.description} />
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
              Additional subjects you teach
            </Typography>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Teaching Standards</InputLabel>
            <Select
              value={formData.teachingStandards}
              label="Teaching Standards"
              name="teachingStandards"
              onChange={handleSelectChange}
            >
              {standards.map((standard) => (
                <MenuItem key={standard._id} value={standard._id}>
                  {standard.name} - {standard.state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ 
              mt: 3,
              mb: 2,
              height: '48px',
              fontSize: '1.1rem',
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Complete Profile'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default CompleteProfile; 